import React, { useImperativeHandle, useRef } from "react";
import { Box, styled, Typography } from "@mui/material";
import { useIsMobile } from "../../../hooks/responsive";
import useWindowDimensions from "../../../hooks/useWindowDimensions";
import { useNavigate, useParams } from "react-router-dom";
import OdysseeCommunityStat from "../OdysseeCommunityStat";

const Container = styled(Box, { shouldForwardProp: (prop) => prop !== "isMobile" })(({ isMobile }) => {
	return {
		display: "flex",
		flexDirection: "row",
		backgroundColor: "#FFFFFE1A",
		borderRadius: 40,
		position: "relative",
		cursor: "pointer",
		zIndex: 2,
		maxWidth: "95%",
		overflow: "scroll",
		msOverflowStyle: "none" /* IE and Edge */,
		scrollbarWidth: "none",
		"&::-webkit-scrollbar": {
			display: "none", // Cache la scrollbar (Chrome/Safari)
		},
	};
});

const Item = styled(Box, { shouldForwardProp: (prop) => prop !== "isMobile" && prop !== "disabled" })(({
	isMobile,
	disabled,
}) => {
	return {
		width: isMobile ? 150 : 200,
		height: 57,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		borderRadius: 40,
		cursor: disabled ? "auto" : "pointer",
	};
});

const Indicator = styled(Box, { shouldForwardProp: (prop) => prop !== "isMobile" })(({ width }) => {
	return {
		width: width,
		height: 57,
		position: "absolute",
		borderRadius: 40,
		background: "linear-gradient(90deg, #EE7992 0%, #F1AE65 50.51%, #F4C54D 100%)",
		transition: "transform 0.3s ease",
	};
});

const MenuOdyssee = React.forwardRef(function MenuOdyssee({ elements, onClick }, ref) {
	const containerRef = useRef(null);
	const [selectedIndex, setSelectedIndex] = React.useState(0);
	const [isInitialized, setIsInitialized] = React.useState(false);
	const isMobile = useIsMobile();
	const { width: screenWidth } = useWindowDimensions();
	const navigate = useNavigate();
	const { odysseeId, section } = useParams();

	const [itemWidth, setItemWidth] = React.useState(isMobile ? 150 : 200);
	const itemWidthRef = React.useRef(null);
	React.useEffect(() => {
		itemWidthRef.current = itemWidth;
	}, [itemWidth]);

	const navigationUrlIndex = React.useMemo(() => {
		if (!section) return 0;
		return elements?.findIndex((elem) => elem?.url === section);
	}, [elements, section]);

	useImperativeHandle(ref, () => {
		return {
			navigate(index) {
				onClick?.({ index });
				onItemClicked(index);
			},
		};
	}, []);

	React.useEffect(() => {
		if (isNaN(navigationUrlIndex)) return;
		if (!!isInitialized) return;
		if (navigationUrlIndex !== selectedIndex) {
			setSelectedIndex(navigationUrlIndex);
			onClick?.({ ...elements[navigationUrlIndex], index: navigationUrlIndex });
			onItemClicked(navigationUrlIndex);
		}
		setIsInitialized(true);
	}, [navigationUrlIndex, selectedIndex, isInitialized]);

	const onItemClicked = (index) => {
		const url = elements[index]?.url;
		if (url) {
			navigate(`/odyssee/${odysseeId}/${url}`);
		} else {
			navigate(`/odyssee/${odysseeId}`);
		}
		if (isMobile) {
			const containerWidth = screenWidth * 0.95;
			containerRef.current?.scrollTo({
				behavior: "smooth",
				left: itemWidthRef.current * index - (containerWidth / 2 - itemWidthRef.current / 2),
			});
		}
		setSelectedIndex(index);
	};

	React.useEffect(() => {}, []);

	React.useEffect(() => {
		if (containerRef.current) {
			if (isMobile) {
				const containerWidth = screenWidth * 0.95;
				containerRef.current?.scrollTo({
					left: itemWidthRef.current * selectedIndex - (containerWidth / 2 - itemWidthRef.current / 2),
				});
			}
			const rect = containerRef.current?.getBoundingClientRect();
			setItemWidth(rect.width / 4);

			// observe size change.
			const observer = new ResizeObserver((args) => {
				const rect = containerRef.current?.getBoundingClientRect();
				setItemWidth(Math.max(600, rect.width) / 4);
			});
			observer.observe(containerRef.current);
			return () => {
				observer.disconnect();
			};
		}
	}, [selectedIndex, containerRef]);

	return (
		<Container isMobile={isMobile} ref={containerRef}>
			<Box sx={{ display: "flex", width: isMobile ? 150 * 4 : "100%" }}>
				<Indicator width={itemWidth} sx={{ transform: `translateX(${selectedIndex * itemWidth}px)` }} />
				{elements.map((item, index) => (
					<Item
						isMobile={isMobile}
						disabled={item?.disabled}
						key={item.id}
						onClick={() => {
							if (!!item?.disabled) return;
							onClick?.({ ...item, index });
							onItemClicked(index);
						}}
						sx={{
							minWidth: 150,
						}}
					>
						<Typography
							sx={{
								position: "relative",
								zIndex: 3,
								transition: "color 0.3s ease",
								userSelect: "none",
								opacity: !!item?.disabled ? 0.5 : 1,
							}}
							fontFamily={"Branding SemiBold"}
							fontSize={18}
							color={index === selectedIndex ? "#182B43" : "#FFFFFF"}
						>
							{item?.name}
						</Typography>

						{item.inLive && (
							<Box
								sx={{
									backgroundColor: "#ef7d8f",
									width: "12px",
									height: "12px",
									minWidth: "12px",
									minHeight: "12px",
									borderRadius: "10px",
									animation: "wave 2s ease-in-out infinite",
									marginLeft: "0.7rem",
								}}
							/>
						)}
					</Item>
				))}
			</Box>
		</Container>
	);
});

export default MenuOdyssee;
